import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { IApiClientError } from "../../shared/ApiClient";
import { useAlerts } from "../../contexts/AlertsContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ButtonType from "../common/button/ButtonType";
import ButtonInfo from "../common/button/ButtonInfo";
import { User } from "../../model/User";
import APIEndPoints from "../../utils/APIEndPoints";
import APIError from "../../utils/APIError";
import AppIcons from "../../utils/AppIcons";
import SETTINGS from "../../globals";
import AppSubSections from "../../utils/AppSubSections";
import AppSections from "../../utils/AppSections";

interface IUsersListElementProps {
  data: User;
  canEdit: boolean;
  navigable: boolean;
  onDelete: () => void;
}

function UsersListElement({
  data,
  canEdit,
  navigable,
  onDelete,
}: IUsersListElementProps) {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const alertsManager = useAlerts();

  // Local state for the editable name
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, [apiClient]);

  if (loading) {
    return <Loading />;
  }

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      console.log(serverError);
      console.log(apiErrors);
    } catch (exception) {
      console.log(exception);
    }
  };

  const handleDelete = () => {
    alertsManager.showDialogWindow({
      type: DialogWindowType.CONFIRM,
      message: "Are you sure you want to remove this user from the list?",
      cancelLabel: "Cancel",
      confirmLabel: "Confirm",
      onConfirm: () => {
        if (!data.id) return;
        apiClient
          ?.delete({
            url: APIEndPoints.USERS.ONE(data.id),
          })
          .then(() => {
            alertsManager.showAlertMessage({
              type: AlertMessageType.SUCCESS,
              message: "User deleted successfully",
            });
            onDelete();
          })
          .catch((error: IApiClientError) => {
            if (!error.processed) {
              processAPIErrors(error);
            }
          });
      },
    });
  };

  return (
    <div
      className="details-section"
      onClick={() => {
        navigate(
          SETTINGS.get_path({
            section: AppSections.USERS,
            id: data.id,
            subsection: AppSubSections.VIEW,
          }),
        );
      }}
    >
      <div className="details-section-title">
        <div className="list-row-title">
          <h3>{data.name + " " + data.surname}</h3>
        </div>
        <div>
          {canEdit && (
            <>
              <Button
                label="Edit"
                purpose={ButtonAction.EDIT}
                type={ButtonType.ICON_TEXT}
                onClick={() => {
                  navigate(
                    SETTINGS.get_path({
                      section: AppSections.USERS,
                      id: data.id,
                      subsection: AppSubSections.EDIT,
                    }),
                  );
                }}
              />
              <Button
                label="Delete"
                purpose={ButtonAction.DELETE}
                type={ButtonType.ICON}
                onClick={handleDelete}
              />
            </>
          )}
        </div>
      </div>

      <div className="list-row-footer flex flex-col gap-2 details-section-content">
        <ButtonInfo>
          {AppIcons.USER} USERNAME: <span> {data.username}</span>
        </ButtonInfo>
        <ButtonInfo>
          {AppIcons.EMAIL} EMAIL: <span>{data.email}</span>
        </ButtonInfo>
        <ButtonInfo>
          {AppIcons.TOOLS} ROLE: <span>{data.role}</span>
        </ButtonInfo>

        {Boolean(data.board_member) && (
          <ButtonInfo>
             {AppIcons.WATCHLIST} Board Member: <span> YES</span>
          </ButtonInfo>

        )}
      </div>
    </div>
  );
}

export default UsersListElement;
